/**
 * MSW build-time guard — the single source of truth for "may this bundle EVER
 * start the MSW mock worker?".
 *
 * MSW fabricates API responses (including alarm verdicts). Serving it from a
 * production bundle would silently violate the no-fake-verdicts rule (audit
 * 2026-07-07). This module has NO msw/handlers import on purpose, so SPA
 * bootstraps can statically import the predicate to gate their dynamic
 * `import('@cguard/api-client/mocks/browser')` without pulling the worker into
 * the production bundle.
 */

export interface MockServerEnv {
  readonly DEV?: boolean | undefined;
  readonly MODE?: string | undefined;
}

/**
 * A bundle is mock-allowed ONLY when it is one of:
 *   - a dev build (`import.meta.env.DEV`), or
 *   - an explicit demo build (`--mode demo` → `import.meta.env.MODE === 'demo'`,
 *     the same signal that bakes `data-mocks="1"` into the demo HTML).
 *
 * There is deliberately NO env-var escape hatch: an earlier draft also accepted
 * `VITE_ENABLE_MSW=1` unconditionally, which meant a production build with that
 * var baked in at build time (a future `--build-arg` or CI-profile typo) would
 * still serve mocks. Mock-allowance is a pure function of the build mode — a
 * plain `pnpm build` (MODE=production, DEV=false) is NEVER allowed, no baked
 * env var and no runtime hint (`?mocks=1`, `<html data-mocks="1">`) can
 * override it, and any build that legitimately needs mocks must be produced
 * with `--mode demo`.
 *
 * The env object is injectable for tests; it defaults to the live build env,
 * read through the same `import.meta` cast the mock handlers use (the api-client
 * tsconfig has no `vite/client` ambient `ImportMeta.env`).
 */
export function isMockServerAllowed(
  env: MockServerEnv = (import.meta as ImportMeta & { env?: MockServerEnv }).env ?? {},
): boolean {
  return env.DEV === true || env.MODE === 'demo';
}
